import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  User,
  Mail,
  Shield,
  Lock,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  MessageSquare,
  FileText,
  Send,
  Phone,
  AlertCircle,
  CheckCircle,
  Clock,
  Infinity,
} from 'lucide-react';
import Header from '../components/Header';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import {
  apiClient,
  ProfileData,
  WabaAccessInfo,
  RecentDispatch,
  UpdateProfileDto,
  ChangePasswordDto,
} from '../services/api';

export default function Profile() {
  const { user, accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [wabaAccess, setWabaAccess] = useState<WabaAccessInfo[]>([]);
  const [recentDispatches, setRecentDispatches] = useState<RecentDispatch[]>([]);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState<'profile' | 'waba' | 'activity'>('profile');

  useEffect(() => {
    loadProfileData();
  }, [accessToken]);

  const loadProfileData = async () => {
    if (!accessToken) return;

    try {
      setLoading(true);

      // Load profile and WABA access in parallel
      const [profileResponse, wabaResponse, dispatchesResponse] = await Promise.all([
        apiClient.getProfile(accessToken),
        apiClient.getMyWabaAccess(accessToken),
        apiClient.getMyRecentDispatches(20, accessToken),
      ]);

      if (profileResponse.success) {
        setProfileData(profileResponse.data);
        setName(profileResponse.data.user.name);
        setEmail(profileResponse.data.user.email);
      }

      if (wabaResponse.success) {
        setWabaAccess(wabaResponse.data);
      }

      if (dispatchesResponse.success) {
        setRecentDispatches(dispatchesResponse.data);
      }
    } catch {
      // Error loading profile data - silently handled
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!accessToken) return;

    // Validate
    if (!name.trim()) {
      setFormError('Nome é obrigatório');
      return;
    }

    if (!email.trim() || !email.includes('@')) {
      setFormError('Email inválido');
      return;
    }

    // Check if there are changes
    if (name === profileData?.user.name && email === profileData?.user.email) {
      setFormError('Nenhuma alteração detectada');
      return;
    }

    try {
      setSaving(true);

      const updateData: UpdateProfileDto = {};
      if (name !== profileData?.user.name) updateData.name = name;
      if (email !== profileData?.user.email) updateData.email = email;

      const response = await apiClient.updateProfile(updateData, accessToken);

      if (response.success) {
        setFormSuccess('Perfil atualizado com sucesso!');
        // Refresh profile data
        await loadProfileData();
      }
    } catch (error: any) {
      setFormError(error.message || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!accessToken) return;

    // Validate
    if (!currentPassword) {
      setPasswordError('Senha atual é obrigatória');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Nova senha deve ter pelo menos 8 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('As senhas não conferem');
      return;
    }

    try {
      setChangingPassword(true);

      const data: ChangePasswordDto = {
        currentPassword,
        newPassword,
      };

      const response = await apiClient.changePassword(data, accessToken);

      if (response.success) {
        setPasswordSuccess('Senha alterada com sucesso!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordForm(false);
      }
    } catch (error: any) {
      setPasswordError(error.message || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-accent-2/20 text-accent-2';
      case 'ADMIN':
        return 'bg-info/20 text-info';
      case 'USER':
        return 'bg-success/20 text-success';
      case 'VIEWER':
        return 'bg-bg-3 text-text-3';
      default:
        return 'bg-bg-3 text-text-3';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-1">
        <Header title="Meu Perfil" />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner size="lg" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-1">
      <Header title="Meu Perfil" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Header Card */}
        <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-accent-1 rounded-full flex items-center justify-center">
              <User size={32} className="text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-text-1">{profileData?.user.name}</h2>
              <p className="text-text-2">{profileData?.user.email}</p>
              <div className="flex gap-2 mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(profileData?.user.role || '')}`}>
                  <Shield size={12} className="mr-1" />
                  {profileData?.user.role}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        {profileData?.statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-bg-2 border border-bg-3 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText size={18} className="text-accent-1" />
                <span className="text-sm text-text-2">Campanhas</span>
              </div>
              <p className="text-2xl font-bold text-text-1">{profileData.statistics.campaignsCreated}</p>
            </div>

            <div className="bg-bg-2 border border-bg-3 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare size={18} className="text-accent-2" />
                <span className="text-sm text-text-2">Templates</span>
              </div>
              <p className="text-2xl font-bold text-text-1">{profileData.statistics.templatesCreated}</p>
            </div>

            <div className="bg-bg-2 border border-bg-3 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Send size={18} className="text-success" />
                <span className="text-sm text-text-2">Disparos (24h)</span>
              </div>
              <p className="text-2xl font-bold text-text-1">{profileData.statistics.totalDispatches24h}</p>
            </div>

            <div className="bg-bg-2 border border-bg-3 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Phone size={18} className="text-accent-2" />
                <span className="text-sm text-text-2">Acessos WABA</span>
              </div>
              <p className="text-2xl font-bold text-text-1">{profileData.statistics.wabaAccessCount}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-bg-3 mb-6">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('profile')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'profile'
                  ? 'border-accent-1 text-accent-1'
                  : 'border-transparent text-text-2 hover:text-text-1'
              }`}
            >
              Dados do Perfil
            </button>
            <button
              onClick={() => setActiveTab('waba')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'waba'
                  ? 'border-accent-1 text-accent-1'
                  : 'border-transparent text-text-2 hover:text-text-1'
              }`}
            >
              Limites WABA
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'activity'
                  ? 'border-accent-1 text-accent-1'
                  : 'border-transparent text-text-2 hover:text-text-1'
              }`}
            >
              Atividade Recente
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Update Profile Form */}
            <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-text-1 mb-4">Atualizar Dados</h3>

              {formError && (
                <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-lg flex items-center gap-2 text-error">
                  <AlertCircle size={18} />
                  {formError}
                </div>
              )}

              {formSuccess && (
                <div className="mb-4 p-3 bg-success/10 border border-success/30 rounded-lg flex items-center gap-2 text-success">
                  <CheckCircle size={18} />
                  {formSuccess}
                </div>
              )}

              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">Nome</label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">Email</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
                  Salvar Alterações
                </button>
              </form>
            </div>

            {/* Change Password Section */}
            <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-1">Alterar Senha</h3>
                <button
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  className="text-accent-1 hover:text-accent-2 text-sm"
                >
                  {showPasswordForm ? 'Cancelar' : 'Alterar'}
                </button>
              </div>

              {showPasswordForm && (
                <>
                  {passwordError && (
                    <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-lg flex items-center gap-2 text-error">
                      <AlertCircle size={18} />
                      {passwordError}
                    </div>
                  )}

                  {passwordSuccess && (
                    <div className="mb-4 p-3 bg-success/10 border border-success/30 rounded-lg flex items-center gap-2 text-success">
                      <CheckCircle size={18} />
                      {passwordSuccess}
                    </div>
                  )}

                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-2 mb-1">Senha Atual</label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full pl-10 pr-10 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-3 hover:text-text-2"
                        >
                          {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-2 mb-1">Nova Senha</label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full pl-10 pr-10 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                          placeholder="Mínimo 8 caracteres"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-3 hover:text-text-2"
                        >
                          {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-2 mb-1">Confirmar Nova Senha</label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={changingPassword}
                      className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      {changingPassword ? <RefreshCw size={18} className="animate-spin" /> : <Lock size={18} />}
                      Alterar Senha
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'waba' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-text-1">Seus Limites de Disparo</h3>

            {wabaAccess.length === 0 ? (
              <div className="bg-bg-2 border border-bg-3 rounded-lg p-6 text-center">
                <Phone size={48} className="mx-auto text-text-3 mb-3" />
                <p className="text-text-2">Você ainda não tem acesso a nenhuma conta WABA.</p>
                <p className="text-text-3 text-sm mt-1">Solicite acesso ao administrador do seu tenant.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {wabaAccess.map((access) => (
                  <div
                    key={access.wabaAccountId}
                    className={`bg-bg-2 border rounded-lg p-4 ${
                      access.isActive ? 'border-bg-3' : 'border-error/30 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          access.isActive ? 'bg-success/20' : 'bg-error/20'
                        }`}>
                          <Phone size={20} className={access.isActive ? 'text-success' : 'text-error'} />
                        </div>
                        <div>
                          <h4 className="font-medium text-text-1">{access.wabaName}</h4>
                          <p className="text-sm text-text-3">{access.phoneNumber}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        access.isActive
                          ? 'bg-success/20 text-success'
                          : 'bg-error/20 text-error'
                      }`}>
                        {access.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-text-3">Limite Diário</p>
                        <p className="text-lg font-semibold text-text-1 flex items-center gap-1">
                          {access.isUnlimited ? (
                            <>
                              <Infinity size={18} className="text-accent-1" />
                              Ilimitado
                            </>
                          ) : (
                            access.dailyLimit.toLocaleString()
                          )}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-text-3">Usado (24h)</p>
                        <p className="text-lg font-semibold text-text-1">
                          {access.usedIn24h.toLocaleString()}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-text-3">Disponível</p>
                        <p className={`text-lg font-semibold ${
                          access.isUnlimited
                            ? 'text-success'
                            : access.remainingIn24h > 0
                            ? 'text-success'
                            : 'text-error'
                        }`}>
                          {access.isUnlimited ? (
                            <span className="flex items-center gap-1">
                              <Infinity size={18} />
                              Ilimitado
                            </span>
                          ) : (
                            access.remainingIn24h.toLocaleString()
                          )}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-text-3">Utilização</p>
                        <div className="flex items-center gap-2">
                          {!access.isUnlimited && (
                            <>
                              <div className="flex-1 h-2 bg-bg-3 rounded-full overflow-hidden">
                                <div
                                  className={`h-full transition-all ${
                                    (access.usedIn24h / access.dailyLimit) * 100 > 90
                                      ? 'bg-error'
                                      : (access.usedIn24h / access.dailyLimit) * 100 > 70
                                      ? 'bg-warning'
                                      : 'bg-success'
                                  }`}
                                  style={{
                                    width: `${Math.min(100, (access.usedIn24h / access.dailyLimit) * 100)}%`,
                                  }}
                                />
                              </div>
                              <span className="text-sm text-text-2">
                                {((access.usedIn24h / access.dailyLimit) * 100).toFixed(0)}%
                              </span>
                            </>
                          )}
                          {access.isUnlimited && (
                            <span className="text-sm text-accent-1">Sem limite</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-text-3 flex items-center gap-1">
              <Clock size={14} />
              Os limites são renovados automaticamente a cada 24 horas (janela rolante).
            </p>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-text-1">Disparos Recentes</h3>

            {recentDispatches.length === 0 ? (
              <div className="bg-bg-2 border border-bg-3 rounded-lg p-6 text-center">
                <Send size={48} className="mx-auto text-text-3 mb-3" />
                <p className="text-text-2">Nenhum disparo realizado ainda.</p>
              </div>
            ) : (
              <div className="bg-bg-2 border border-bg-3 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-bg-3">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">WABA</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">Campanha</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-text-2 uppercase">Qtd</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-bg-3">
                    {recentDispatches.map((dispatch, index) => (
                      <tr key={index} className="hover:bg-bg-3/50">
                        <td className="px-4 py-3 text-sm text-text-1">{formatDate(dispatch.date)}</td>
                        <td className="px-4 py-3 text-sm text-text-2">{dispatch.wabaName}</td>
                        <td className="px-4 py-3 text-sm text-text-2">
                          {dispatch.campaignName || <span className="text-text-3">-</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-1 text-right font-medium">
                          {dispatch.count.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
